import { createContextMenuDynamicOptions } from '@components/map/contextMenu/builders/utils';
import { getContextMenuCameraLabel } from '@utils/sensors/camera/camera.utils';
import { getContextMenuNeutralLabel } from '@utils/sensors/neutral/neutral.utils';
import { getSensorUniqueKey } from '@utils/sensors/sensors.utils';
import { getTargetIdentification, isDrone } from '@utils/target.utils';

import { Target } from '@/types/c2/c2.types';
import { Mark, MarkEnum } from '@/types/commons/commons.types';
import { MapIdEnum, OperationalMapId } from '@/types/map.types';
import { MenuItemOption } from '@/types/menu.types';
import { HistoryPopupData, PopupType, TargetInfoData } from '@/types/popup.types';
import { CameraConfiguration } from '@/types/sensor/configuration.types';
import { IdentificationEnum } from '@/types/sensor/identification.types';
import { ActiveMonitoringSensorByType } from '@/types/sensor/monitoring.types';
import { TargetLocation } from '@/types/sensor/sensor.types';
import { SensorTypeEnum, SpecificStateEnum, TrackingStatusEnum } from '@/types/sensor/sensors';
import { CameraStatus } from '@/types/sensor/status.types';

import { FeatureTypeToContextComponentEnum, MenuBuilder, MenuKey } from '../type';
import {
  buildCreateEventOption,
  buildDisengageOption,
  buildEngageOption,
  buildMeasureOption,
  buildPointCUASOption,
  buildQrCodeOption,
  CommonContextMenuContext,
  HasDisengageContext,
  HasEngageContext,
  HasPointCUASContext,
} from './common';

export type TargetContextMenuContextCore = {
  accessNeutralCommand: boolean;
  ladContextIsPresent: boolean;
  canManageClearance: boolean;
  canMarkTracks: boolean;
  isAdmin: boolean;
  selectedTarget: Target<TargetLocation>;
  targetInfo: PopupType | null;
  droneHistoryPopup: PopupType | null;
  healthySkyJackerSensors: ActiveMonitoringSensorByType<SensorTypeEnum.SKY_JACKER>[];
  handleOpenTargetInfo: () => void;
  handleTracking: (cameraConfiguration: CameraConfiguration, cameraStatus: CameraStatus) => void;
  handleNeutralization: (monitoringSensor: ActiveMonitoringSensorByType<SensorTypeEnum.SKY_JACKER>) => void;
  handleLAD: () => void;
  handleOpenHistoryPopup: () => void;
  includes: (marks: Mark[], markType: MarkEnum) => boolean; //todo do not pass this function move to utils ? just pass the value
  handleUpdateOffset: () => void;
  handleShareTargetHoloview: () => void;
  handleOpenTargetControlPopup: () => void;
  handleJsonExport: () => void;
  organization: string | null;
};

export type TargetContextMenuContext = CommonContextMenuContext &
  HasPointCUASContext &
  HasEngageContext &
  HasDisengageContext &
  TargetContextMenuContextCore;

export type TargetContextMenuStaticOptions = {
  info: boolean;
  history: boolean;
  jsonExport: boolean;
};

export type TargetContextMenuDynamicOptions = {
  updateOffset: boolean;
  shareOnHoloview: boolean;
  engage: boolean;
  disengage: boolean;
  openDroneControl: boolean;
  pointCUAS: boolean;
  cameraTrack: boolean;
  doubtCheck: boolean;
  manageClearance: boolean;
  neutralize: boolean;
};

export type TargetContextMenuOptions = TargetContextMenuStaticOptions & TargetContextMenuDynamicOptions;

export const TARGET_DEFAULT_CONTEXT_MENU_DYNAMIC_OPTIONS: TargetContextMenuDynamicOptions = {
  updateOffset: true,
  shareOnHoloview: true,
  engage: true,
  disengage: true,
  openDroneControl: true,
  pointCUAS: true,
  cameraTrack: true,
  doubtCheck: true,
  manageClearance: true,
  neutralize: true,
};

const allTrue = createContextMenuDynamicOptions(TARGET_DEFAULT_CONTEXT_MENU_DYNAMIC_OPTIONS, true);
const allFalse = createContextMenuDynamicOptions(TARGET_DEFAULT_CONTEXT_MENU_DYNAMIC_OPTIONS, false);
export const targetContextMenuConfig: Record<OperationalMapId, TargetContextMenuDynamicOptions> = {
  [MapIdEnum.LIVE]: allTrue,
  [MapIdEnum.LAD]: allTrue,
  [MapIdEnum.REPLAY]: allFalse,
  [MapIdEnum.STATISTICS]: allFalse,
};

const buildOpenTargetInfoOption: MenuBuilder<FeatureTypeToContextComponentEnum.TARGET>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'info',
  {
    label: 'contextmenu.actions.target.targetInfo',
    isDisabled: !!ctx.targetInfo && (ctx.targetInfo.data as TargetInfoData).targetId === ctx.selectedTarget.id,
    onAction: ctx.handleOpenTargetInfo,
  },
];

const buildOpenDroneControlOption: MenuBuilder<FeatureTypeToContextComponentEnum.TARGET>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'openDroneControl',
  {
    label: 'contextmenu.actions.target.takeDroneControl',
    isHidden: ctx.selectedTarget.trackIds.every((track) => track.sensorType !== SensorTypeEnum.GROUND_STATION),
    onAction: ctx.handleOpenTargetControlPopup,
  },
];

const buildCameraTrackOption: MenuBuilder<FeatureTypeToContextComponentEnum.TARGET>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'cameraTrack',
  {
    label: 'contextmenu.actions.target.camera.cameraTrack',
    isHidden: !ctx.accessCamCommand,
    isDisabled: ctx.movableLadCameras.length === 0,
    subMenuItems: ctx.movableLadCameras.reduce(
      (acc, current) => ({
        ...acc,
        [getSensorUniqueKey(current)]: {
          label: getContextMenuCameraLabel(current),
          shouldNotTranslate: true,
          isActive:
            current.status.specificState === SpecificStateEnum.TRACKING &&
            current.status.trackingStatus.sensorTrackingStatus.currentTrackedObjectId === ctx.selectedTarget.id,
          onAction: () => ctx.handleTracking(current.configuration, current.status),
        },
      }),
      {},
    ),
  },
];

const buildNeutralizeOption: MenuBuilder<FeatureTypeToContextComponentEnum.TARGET>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'neutralize',
  {
    label: 'contextmenu.actions.target.neutral.neutralize',
    isHidden: !ctx.accessNeutralCommand || ctx.healthySkyJackerSensors.length === 0,
    subMenuItems: ctx.healthySkyJackerSensors.reduce(
      (acc, current) => ({
        ...acc,
        [getSensorUniqueKey(current)]: {
          label: getContextMenuNeutralLabel(current),
          shouldNotTranslate: true,
          isActive:
            current.status.trackingStatus.globalSensorTrackingState == TrackingStatusEnum.ACTIVE &&
            current.status.trackingStatus.currentTrackedObjectId === ctx.selectedTarget.id,
          onAction: () => ctx.handleNeutralization(current),
        },
      }),
      {},
    ),
  },
];

const buildDoubtCheckOption: MenuBuilder<FeatureTypeToContextComponentEnum.TARGET>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'doubtCheck',
  {
    label: `contextmenu.actions.target.c-uas-doubt`,
    isHidden: !ctx.accessCamCommand,
    isDisabled: !ctx.ladContextIsPresent,
    onAction: ctx.handleLAD,
  },
];

const buildHistoryOption: MenuBuilder<FeatureTypeToContextComponentEnum.TARGET>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'history',
  {
    label: `contextmenu.actions.target.history`,
    isHidden: !isDrone(ctx.selectedTarget, ctx.organization) || ctx.selectedTarget.displayId === null,
    isDisabled:
      !!ctx.droneHistoryPopup &&
      (ctx.droneHistoryPopup.data as HistoryPopupData).identifiers.includes(ctx.selectedTarget.id),
    onAction: ctx.handleOpenHistoryPopup,
  },
];

const buildShareOnHoloviewOption: MenuBuilder<FeatureTypeToContextComponentEnum.TARGET>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'shareOnHoloview',
  {
    label: 'contextmenu.actions.target.broadcastTarget.shareHoloview',
    isHidden: !ctx.canMarkTracks,
    isDisabled: ctx.includes(ctx.selectedTarget.marks, MarkEnum.CRISIS),
    onAction: ctx.handleShareTargetHoloview,
  },
];

const buildUpdateOffsetOption: MenuBuilder<FeatureTypeToContextComponentEnum.TARGET>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'updateOffset',
  {
    label: 'contextmenu.actions.target.updateOffset',
    // TODO HLF#31752 No idea how to determine if target comes from ADSB/AVISO
    isDisabled: getTargetIdentification(ctx.selectedTarget, ctx.organization) !== IdentificationEnum.AIRCRAFT,
    onAction: ctx.handleUpdateOffset,
  },
];

const buildJsonExportOption: MenuBuilder<FeatureTypeToContextComponentEnum.TARGET>['build'] = (
  ctx,
): [MenuKey, MenuItemOption] => [
  'jsonExport',
  {
    label: 'contextmenu.actions.target.broadcastTarget.jsonExport',
    isHidden: !ctx.isAdmin,
    onAction: ctx.handleJsonExport,
  },
];

export const targetBuilders: MenuBuilder<FeatureTypeToContextComponentEnum.TARGET>[] = [
  {
    order: 1,
    build: buildOpenTargetInfoOption,
  },
  {
    order: 2,
    build: buildQrCodeOption,
  },
  {
    order: 3,
    build: buildMeasureOption,
  },
  {
    order: 4,
    build: buildCreateEventOption,
  },
  {
    order: 5,
    separator: true,
  },
  {
    order: 6,
    build: buildUpdateOffsetOption,
  },
  {
    order: 7,
    build: buildShareOnHoloviewOption,
  },
  {
    order: 8,
    build: buildEngageOption,
  },
  {
    order: 9,
    build: buildDisengageOption,
  },
  {
    order: 10,
    separator: true,
  },
  {
    order: 11,
    build: buildOpenDroneControlOption,
  },
  {
    order: 12,
    build: buildPointCUASOption,
  },
  {
    order: 13,
    build: buildCameraTrackOption,
  },
  {
    order: 14,
    build: buildDoubtCheckOption,
  },
  {
    order: 15,
    build: buildNeutralizeOption,
  },
  {
    order: 16,
    separator: true,
  },
  {
    order: 17,
    build: buildHistoryOption,
  },
  {
    order: 18,
    separator: true,
  },
  {
    order: 19,
    build: buildJsonExportOption,
  },
];
